import { CeType } from '@wix/adi-core-types';
import { WIX_PORTFOLIO } from '@wix/app-definition-ids';
import type {
  ColorPalette,
  CompData,
  CompLayout,
  CompRef,
  DocumentServicesObject,
  MenuData,
  MenuItem,
  SerializedCompStructure,
  SerializedPageStructure,
  StyleRef,
  StyleRefOrStyleRefs,
} from '@wix/document-services-types';
import {
  AiMediaData,
  PresetContentFieldRole,
  type LegalPageLinkData,
} from '@wix/editor-content-injector';
import type { PageSectionContent } from '@wix/editor-content-provider';
import { ErrorReporter } from '@wix/editor-error-reporter';
import {
  mapStructureByCompType,
  type KitDefinition,
  type KitSiteStructure,
} from '@wix/editor-kits';
import {
  EditorType,
  InstallInitiator,
  InstallationOriginType,
} from '@wix/platform-editor-sdk';
import _ from 'lodash';
import { ImagesMap } from '../aiImageGenerator/types';
import {
  COMP_TYPES,
  PROPS_TO_DELETE_FROM_STRUCTURE,
  RESTRICTED_SECTION_CE_TYPES_TO_ADD,
  SECTION_MIN_HEIGHT,
} from '../siteGeneratorUtilsConsts';
import {
  ContentTypes,
  ExtendedContentTypes,
  type AddedPageWithRef,
  type FooterPresetDefinition,
  type FooterPresetStructure,
  type FullHomepageStructure,
  type GeneratedHomepage,
  type GeneratedPage,
  type HeaderPresetDefinition,
  type HeaderPresetStructure,
  type LayoutFamilyDefinition,
  type LockedPresetIds,
  type PageStructure,
  type PresetStructure,
  type SectionPresetDefinition,
  type SectionPresetStructure,
  type SiteTheme,
} from '../types';
import { fetchPresetStructures } from './fetchDataUtils';
import { getBlankPageStructure } from './getBlankPageStructure';
import { getBlankSectionStructure } from './getBlankSectionStructure';
import { adjustHeader, isFooter, isHeader } from './headerAndFooterUtils';
import {
  DEFAULT_LEGAL_PAGES,
  LEGAL_PAGES_BY_LANG,
  LEGAL_PAGES_TO_APPS,
  LegalPage,
} from '../legalPagesConsts';

export const chooseRandomIndex = (max: number) => {
  return Math.floor(Math.random() * max);
};

function getMainMenu(ds: DocumentServicesObject): MenuData {
  return (
    ds.menu.getByType('MAIN_MENU')[0] || ds.menu.getById('CUSTOM_MAIN_MENU')
  );
}

const cleanId = (id: string) => id.replace('#', '');

function getSectionAnchor(
  ds: DocumentServicesObject,
  homepageSections: CompRef[],
) {
  return homepageSections.map((anchorRef) => {
    const data = ds.components.anchor.get(anchorRef);
    const pageId = ds.components.getPage(anchorRef).id;

    return {
      anchorName: data.name,
      pageId: `#${pageId}`,
      anchorDataId: `#${data.id}`,
      y: ds.components.layout.getRelativeToScreen(anchorRef)?.y || 0,
    };
  });
}

function addingSectionsToMenu(
  ds: DocumentServicesObject,
  menuId: string,
  sectionAnchorData: {
    y: number;
    pageId: string;
    type: string;
    anchorName: string;
    anchorDataId: string;
  }[],
  homepageSectionsItemIds: string[],
) {
  sectionAnchorData.forEach((sectionLink) => {
    const menuItemId = ds.menu.addItem(menuId, {
      link: sectionLink,
      label: sectionLink.anchorName,
      isVisible: true,
      isVisibleMobile: true,
    });
    homepageSectionsItemIds.push(menuItemId);
  });
}

function reorderMenu(
  ds: DocumentServicesObject,
  homepageSectionsItemIds: string[],
  mainMenu: MenuData,
  menuId: string,
) {
  const sortedMenu: MenuItem[] = [];
  const menuItems = ds.menu.getAll()[0].items;
  sortedMenu.push(menuItems[0]);

  menuItems.forEach((item) => {
    if (homepageSectionsItemIds.includes(item.id as string)) {
      sortedMenu.push(item);
    }
  });

  menuItems.forEach((item) => {
    if (!sortedMenu.includes(item)) {
      sortedMenu.push(item);
    }
  });

  ds.menu.update(menuId, {
    ...mainMenu,
    items: Object.values(sortedMenu),
  });
}

export const addSectionsToMenuIfNeeded = async (
  ds: DocumentServicesObject,
  homepageRef: CompRef,
) => {
  try {
    const pagesList = ds.pages.getPageIdList();
    if (pagesList.length >= 4) return;
    const mainMenu = getMainMenu(ds);
    const menuId = cleanId(mainMenu.id);
    const homepageSections = ds.components.get.byType(
      COMP_TYPES.SECTION,
      homepageRef,
    );
    const sectionAnchorData = getSectionAnchor(ds, homepageSections)
      .map((sectionAnchor) => ({
        ...sectionAnchor,
        type: 'AnchorLink',
      }))
      .sort((a, b) => a.y - b.y);
    await ds.transactions.run(async () => {
      const homepageSectionsItemIds: string[] = [];
      addingSectionsToMenu(
        ds,
        menuId,
        sectionAnchorData,
        homepageSectionsItemIds,
      );
      reorderMenu(ds, homepageSectionsItemIds, mainMenu, menuId);
    });
  } catch (e) {
    ErrorReporter.captureException(e, {
      tags: {
        siteGenerationApiFlow: true,
      },
    });
  }
};

const adjustComponentsFollowingLayouts = (
  compStructures: SerializedCompStructure[],
): SerializedCompStructure[] => {
  return compStructures.reduce((acc, structure) => {
    const lastSection = acc[acc.length - 1];
    const structureLayout = structure.layout;
    if (!structureLayout) {
      // TODO: implement for editor3
      return [...acc, structure];
    }
    if (lastSection) {
      const lastSectionLayout = lastSection.layout;
      if (lastSectionLayout) {
        // TODO: implement for editor3
        structureLayout.y = lastSectionLayout.y + lastSectionLayout.height;
      }
    } else {
      structureLayout.y = 0;
    }
    return [...acc, structure];
  }, [] as SerializedCompStructure[]);
};

export const createPageStructure = (
  pageUriSEO: string,
  sections: PresetStructure[],
  isMobile: boolean,
): SerializedPageStructure => {
  const pageStructure = getBlankPageStructure(pageUriSEO, [], isMobile);
  pageStructure.components = adjustComponentsFollowingLayouts(
    sections.map(({ structure, mobileStructure }) =>
      isMobile ? mobileStructure : structure,
    ),
  );
  // @ts-ignore
  if (Array.isArray(pageStructure.mobileComponents)) {
    // @ts-ignore
    pageStructure.mobileComponents = adjustComponentsFollowingLayouts(
      sections.map(({ mobileStructure }) => mobileStructure),
    );
    const lastSection =
      // @ts-ignore
      pageStructure.mobileComponents[pageStructure.mobileComponents.length - 1];
    const lastSectionLayout = lastSection.layout;
    if (lastSectionLayout) {
      // TODO: implement for editor3
      pageStructure.layout.height =
        lastSectionLayout.y + lastSectionLayout.height;
    }
  }
  const lastSection =
    pageStructure.components[pageStructure.components.length - 1];
  const lastSectionLayout = lastSection.layout;
  if (lastSectionLayout) {
    // TODO: implement for editor3
    pageStructure.layout.height =
      lastSectionLayout.y + lastSectionLayout.height;
  }

  return pageStructure;
};

export const transformCompStructure = (
  structure: SerializedCompStructure,
  transformation: (comp: SerializedCompStructure) => SerializedCompStructure,
): SerializedCompStructure => {
  const transformedStructure = transformation(_.cloneDeep(structure));
  if (transformedStructure.components) {
    transformedStructure.components = transformedStructure.components.map(
      (component) => transformCompStructure(component, transformation),
    );
  }
  return transformedStructure;
};

export const addContentRoleDataToSection = (
  sectionStructure: SerializedCompStructure,
  ceType: CeType,
): SerializedCompStructure => {
  return {
    ...sectionStructure,
    contentRole: {
      ...sectionStructure.contentRole,
      complexity: 'simple',
      contentRole: ceType,
      type: 'ContainerRole',
    },
  };
};

export const removeDeadComponents = (
  compStructure: SerializedCompStructure,
): void => {
  if (!compStructure.components?.length) {
    return;
  }
  compStructure.components = compStructure.components.filter(
    ({ componentType }) => componentType !== COMP_TYPES.DEAD_COMP,
  );
  compStructure.components.forEach(removeDeadComponents);
};

export const deletePropsFromStructure = (
  compStructure: SerializedCompStructure,
) => {
  for (const prop of PROPS_TO_DELETE_FROM_STRUCTURE) {
    if (compStructure.hasOwnProperty(prop)) {
      delete compStructure[prop];
    }
  }

  compStructure.components?.forEach((comp) => {
    deletePropsFromStructure(comp);
  });
};

const replaceChildComps = (
  ds: DocumentServicesObject,
  ref: CompRef,
  replacement: SerializedCompStructure,
) => {
  const childComps = ds.components.getChildren(ref);
  childComps.forEach((component) => ds.components.remove(component));

  replacement.components?.forEach((component) => {
    ds.components.add(ref, component);
  });
};

export const replaceStyle = (
  ds: DocumentServicesObject,
  ref: CompRef,
  style?: string | StyleRefOrStyleRefs,
) => {
  if (!style) return;
  if (typeof style === 'string') {
    if (ds.theme.styles.get(style)) {
      ds.components.style.connectToTheme(ref, style);
    }
  } else {
    ds.components.style.update(ref, style as StyleRef);
  }
};

export const replaceDesign = (
  ds: DocumentServicesObject,
  ref: CompRef,
  design?: SerializedCompStructure['design'],
) => {
  if (!design) return;
  ds.components.design.update(ref, design);
};

const replaceLayout = (
  ds: DocumentServicesObject,
  ref: CompRef,
  layout?: Partial<CompLayout>,
) => {
  if (!layout) return;
  ds.components.layout.update(ref, {
    height: layout.height,
    fixedPosition: layout.fixedPosition,
  });
};

const adjustBehaviors = (
  ds: DocumentServicesObject,
  ref: CompRef,
  compStructure: SerializedCompStructure,
) => {
  if (!compStructure.behaviors) return;
  const parsedBehaviorsItems = JSON.parse(compStructure.behaviors.items);
  //TODO: remove ts-ignore when types are fixed (https://wix.slack.com/archives/C9N00LLE6/p1701783454664289)
  ds.components.behaviors.update(
    ref,
    parsedBehaviorsItems[0],
    //@ts-ignore
    parsedBehaviorsItems[0].action,
    //@ts-ignore
    parsedBehaviorsItems[0].viewMode,
  );
};

export const replaceHeaderOrFooter = (
  ds: DocumentServicesObject,
  ref: CompRef,
  compStructure: SerializedCompStructure,
) => {
  replaceLayout(ds, ref, compStructure.layout);
  adjustBehaviors(ds, ref, compStructure);
  replaceStyle(ds, ref, compStructure.style);
  replaceDesign(ds, ref, compStructure.design);
  fixPresetStructureApplicationIds(ds, compStructure);
  replaceChildComps(ds, ref, compStructure);
};

export const replaceHeader = (
  ds: DocumentServicesObject,
  headerStructure: SerializedCompStructure,
) => {
  if (headerStructure.componentType !== COMP_TYPES.HEADER) {
    throw new Error(
      `replaceHeader called with ${headerStructure.componentType} type`,
    );
  }
  replaceHeaderOrFooter(ds, ds.siteSegments.getHeader(), headerStructure);
};

export const replaceFooter = (
  ds: DocumentServicesObject,
  footerStructure?: SerializedCompStructure,
) => {
  if (!footerStructure?.componentType) {
    return;
  }
  if (footerStructure.componentType !== COMP_TYPES.FOOTER) {
    throw new Error(
      `replaceFooter called with ${footerStructure.componentType} type`,
    );
  }
  replaceHeaderOrFooter(ds, ds.siteSegments.getFooter(), footerStructure);
};

export const setSiteTheme = (
  ds: DocumentServicesObject,
  kitDefinition: KitDefinition,
  logoPalette?: Partial<ColorPalette>,
) => {
  const siteTheme = getSiteTheme(ds, kitDefinition, logoPalette);

  ds.theme.colors.update(siteTheme.colors);
  if (siteTheme.linkedColors) {
    // @ts-expect-error
    ds.site.features.update('themeConfig', {
      colorMapping: siteTheme.linkedColors,
    });
  }
  ds.theme.textThemes.update(siteTheme.textThemes);
  Object.entries(siteTheme.styles).forEach(([styleId, styleValue]) => {
    ds.theme.styles.update(styleId, styleValue);
  });
};

export function isExist<T>(value: T | null): value is T {
  return Boolean(value);
}

export function isHomepageStructure(
  pageStructure: PageStructure | FullHomepageStructure,
): pageStructure is FullHomepageStructure {
  return !!(pageStructure as FullHomepageStructure).header;
}

export function isGeneratedHomepageStructure(
  generatedPage: GeneratedHomepage | GeneratedPage,
): generatedPage is GeneratedHomepage {
  return Boolean(
    (generatedPage as GeneratedHomepage).header ||
      (generatedPage as GeneratedHomepage).footer,
  );
}

export const getKitSiteStructure = ({
  header,
  sections,
  footer,
}: FullHomepageStructure): KitSiteStructure => {
  return {
    header: header && {
      structure: header?.structure,
      mobileStructure: header?.mobileStructure,
    },
    sections: sections
      .filter(({ structure, mobileStructure }) => structure && mobileStructure)
      .map(({ structure, mobileStructure }) => ({
        structure,
        mobileStructure,
      })),
    footer: footer && {
      structure: footer?.structure,
      mobileStructure: footer?.mobileStructure,
    },
  };
};

export const getSectionsStructures = async (
  sectionPresets: SectionPresetDefinition[],
  sectionContents: PageSectionContent[],
): Promise<SectionPresetStructure[]> => {
  return Promise.all(
    sectionPresets.map(async (preset, index) => {
      const { title, presetJsonUrl, presetMobileJsonUrl, _id } = preset;
      const [sectionStructure, mobileStructure] = await fetchPresetStructures([
        presetJsonUrl,
        presetMobileJsonUrl,
      ]);
      const structure = addContentRoleDataToSection(
        sectionStructure,
        sectionContents[index].ceType,
      );
      return {
        ...sectionContents[index],
        title,
        id: _id,
        structure,
        mobileStructure,
      };
    }),
  ).then((sections) =>
    sections.filter((s): s is SectionPresetStructure => isExist(s)),
  );
};

export const replaceSectionsAnchorNames = (
  sections: SectionPresetStructure[],
  t: (key: string) => string,
): void => {
  sections.forEach((section) => {
    const { name, ceType, structure } = section;
    const key = `Section_Name_${ceType}`;
    const newName = name || t(key) || ceType;
    section.name = newName;
    if (structure.anchor) {
      structure.anchor.name = newName;
    }
  });
};

const fixDataApplicationId = (
  ds: DocumentServicesObject,
  compData: CompData,
): void => {
  if (!compData || !compData?.applicationId || !compData?.appDefinitionId) {
    return;
  }
  const applicationId = ds.platform.getAppDataByAppDefId(
    compData.appDefinitionId,
  )?.applicationId;
  if (!applicationId) {
    return;
  }
  compData.applicationId = `${applicationId}`;
};

export const fixPresetStructureApplicationIds = (
  ds: DocumentServicesObject,
  presetStructure: SerializedCompStructure | SerializedPageStructure,
): void => {
  fixDataApplicationId(ds, presetStructure.data);
  presetStructure.components?.forEach((childStructure) => {
    fixPresetStructureApplicationIds(ds, childStructure);
  });
};

export const isAppInstalled = (
  ds: DocumentServicesObject,
  appDefId: string,
): boolean => {
  return ds.platform.isAppActive(appDefId) || ds.tpa.app.isInstalled(appDefId);
};

export const retrieveAppDefIdsFromStructure = (
  compStructure: SerializedCompStructure,
): string[] => {
  const appDefIds: string[] = [];
  if (compStructure.data?.appDefinitionId) {
    appDefIds.push(compStructure.data.appDefinitionId);
  } else if (compStructure.data?.applicationId) {
    appDefIds.push(compStructure.data.applicationId);
  }
  compStructure.components?.forEach((comp) => {
    appDefIds.push(...retrieveAppDefIdsFromStructure(comp));
  });
  return [...new Set(appDefIds)];
};

export const getHomepagePresetsLocked = (
  headers: HeaderPresetDefinition[],
  sectionsMatrix: SectionPresetDefinition[][],
  footers: FooterPresetDefinition[],
  lockedPresetsIds: LockedPresetIds,
): {
  headerPreset: HeaderPresetDefinition | null;
  sectionsPresets: Array<SectionPresetDefinition | null>;
  footerPreset: FooterPresetDefinition | null;
} => {
  return {
    headerPreset:
      headers.find((header) => header._id === lockedPresetsIds.header) ||
      _.sample(headers) ||
      null,
    sectionsPresets: sectionsMatrix.map(
      (sectionList) =>
        sectionList.find((section) =>
          lockedPresetsIds.sections.find(
            (presetId) => section._id === presetId,
          ),
        ) || sectionList[0],
    ),
    footerPreset:
      footers.find((footer) => footer._id === lockedPresetsIds.footer) ||
      _.sample(footers) ||
      null,
  };
};

export const navigateToPage = async (
  ds: DocumentServicesObject,
  pageRef: CompRef,
) => {
  await new Promise<void>((resolve) =>
    ds.pages.navigateTo(pageRef.id, resolve),
  );
  await ds.waitForChangesAppliedAsync();
};

export const removePreviousPage = async (
  ds: DocumentServicesObject,
  currentHomepageId: string,
) => {
  ds.pages.remove(currentHomepageId);
  await ds.waitForChangesAppliedAsync();
};

export const adjustAndReplaceHeader = (
  ds: DocumentServicesObject,
  header: HeaderPresetStructure | null,
): void => {
  if (!header?.structure) {
    return;
  }
  header.structure = adjustHeader(header.structure, (appDefId: string) =>
    isAppInstalled(ds, appDefId),
  );
  header.mobileStructure = adjustHeader(
    header.mobileStructure,
    (appDefId: string) => isAppInstalled(ds, appDefId),
  );
  replaceHeader(ds, header.structure);
};

export const getSiteTheme = (
  ds: DocumentServicesObject,
  kitDefinition: KitDefinition,
  paletteFromLogo?: Partial<ColorPalette>,
): SiteTheme => {
  const logoPalette = paletteFromLogo || {};
  return {
    textThemes: _.defaultsDeep(
      {},
      kitDefinition.fonts,
      ds.theme.textThemes.getAll(),
    ),
    colors: { ...kitDefinition.colors, ...logoPalette },
    linkedColors: kitDefinition.linkedColors,
    styles: kitDefinition.themeStyles,
  };
};

export const setUsesNewAnimations = (ds: DocumentServicesObject) => {
  const masterPageRef = ds.components.get.byId(
    ds.pages.getMasterPageId(),
  ) as CompRef;
  ds.components.data.update(masterPageRef, { usesNewAnimations: true });
};

const getCustomAppPageAsHomepage = (
  appDefIds: string[],
  sections: SectionPresetStructure[],
): { appDefId: string; ceType: CeType } | null => {
  if (
    appDefIds.includes(WIX_PORTFOLIO) &&
    sections.find(({ ceType }) => ceType === CeType.Portfolio_Widget)
  ) {
    return {
      appDefId: WIX_PORTFOLIO,
      ceType: CeType.Portfolio_Widget,
    };
  }
  return null;
};

const applyWidgetCustomData = (
  ds: DocumentServicesObject,
  appPageRef: CompRef,
  sourceSection: SectionPresetStructure,
) => {
  const refComps = ds.components.get.byType(
    COMP_TYPES.REF_COMPONENT,
    appPageRef,
  );
  const structureRefComponents = mapStructureByCompType(
    sourceSection.structure,
  )['wysiwyg.viewer.components.RefComponent'];
  if (refComps.length !== 1 || structureRefComponents?.length !== 1) {
    return;
  }
  const [compRef] = refComps;
  const [sourceRefCompStructure] = structureRefComponents;
  if (!sourceRefCompStructure.presets) {
    return;
  }
  ds.appStudioWidgets.presets.change(
    compRef,
    sourceRefCompStructure.presets.style,
    sourceRefCompStructure.presets.layout,
  );

  let mobileVariant = ds.variants.mobile.get();
  if (!mobileVariant) {
    mobileVariant = ds.variants.mobile.create();
  }
  const mobileVariantId = mobileVariant.id;
  if (!mobileVariantId) {
    return;
  }
  const mobileScopedPreset =
    sourceRefCompStructure.scopedPresets?.[mobileVariantId];
  if (mobileScopedPreset) {
    const compVariantRef = ds.components.variants.getPointer(compRef, [
      mobileVariant,
    ]);
    ds.appStudioWidgets.presets.change(
      compVariantRef,
      mobileScopedPreset.style,
      mobileScopedPreset.layout,
    );
  }
};

export const setPageTextsSource = (
  ds: DocumentServicesObject,
  pageRef: CompRef,
) => {
  ds.components.get.byType(COMP_TYPES.TEXT, pageRef).forEach((textRef) => {
    ds.components.features.update(textRef, 'source', {
      type: 'Source',
      original: 'preset',
      current: 'ai',
      changed: false,
    });
  });
};

export const handleCustomAppPageAsHomepage = (
  ds: DocumentServicesObject,
  appDefIds: string[],
  sections: SectionPresetStructure[],
): CompRef | null => {
  const customAppPageAsHomepage = getCustomAppPageAsHomepage(
    appDefIds,
    sections,
  );
  if (!customAppPageAsHomepage) {
    return null;
  }
  const appPageId = ds.pages
    .getPagesData()
    .find(
      ({ managingAppDefId }) =>
        managingAppDefId === customAppPageAsHomepage.appDefId,
    )?.id;
  if (!appPageId) {
    return null;
  }
  const appPageRef = ds.components.get.byId(appPageId) as CompRef;
  const appPageWidgetIndex = sections.findIndex(
    ({ ceType }) => ceType === customAppPageAsHomepage.ceType,
  );
  if (appPageWidgetIndex < 0) {
    return null;
  }
  verifyPageAppWithSections(ds, appPageRef);
  applyWidgetCustomData(ds, appPageRef, sections[appPageWidgetIndex]);
  if (sections.length === 1) {
    return appPageRef;
  }
  const sectionsAbove = sections.slice(0, appPageWidgetIndex);
  const sectionsBelow = sections.slice(appPageWidgetIndex + 1, sections.length);
  unshiftSections(ds, appPageRef, sectionsAbove);
  appendSections(ds, appPageRef, sectionsBelow);
  return appPageRef;
};

export const verifyPageAppWithSections = (
  ds: DocumentServicesObject,
  pageAppRef: CompRef,
) => {
  const pageSections = ds.components.get.byType(COMP_TYPES.SECTION, pageAppRef);
  if (pageSections.length) return;

  const pageHeight = ds.components.layout.get(pageAppRef)?.height;

  const pageComponents = ds.components.getChildren(pageAppRef);

  const pageHeightByComponents = Math.max(
    ...pageComponents
      .map(ds.components.layout.get)
      .map((layout) => (layout?.y || 0) + (layout?.height || 0)),
  );

  const blankSectionStructure = getBlankSectionStructure({
    y: 0,
    height: Math.max(
      pageHeight || 0,
      pageHeightByComponents,
      SECTION_MIN_HEIGHT,
    ),
    name: ds.pages.data.get(pageAppRef.id).title,
  });

  const sectionRef = ds.components.add(pageAppRef, blankSectionStructure);

  pageComponents.forEach((compRef) =>
    ds.components.setContainer(compRef, sectionRef),
  );
};

export const unshiftSections = (
  ds: DocumentServicesObject,
  pageRef: CompRef,
  sections: SectionPresetStructure[],
): void => {
  const totalAddedSectionsHeight = sections.reduce((acc, { structure }) => {
    const layout = structure.layout;
    if (!layout) {
      // TODO: implement for editor3
      return acc;
    }
    layout.y = acc;
    return acc + layout.height;
  }, 0);
  const pageSections = ds.components.get.byType(COMP_TYPES.SECTION, pageRef);
  pageSections.forEach((pageSection) => {
    const pageSectionY = ds.components.layout.get(pageSection)!.y;
    ds.components.layout.update(pageSection, {
      y: pageSectionY + totalAddedSectionsHeight,
    });
  });
  sections.forEach(({ structure }, index) => {
    ds.components.add(pageRef, structure, undefined, index);
  });
};

export const appendSections = (
  ds: DocumentServicesObject,
  pageRef: CompRef,
  sections: SectionPresetStructure[],
): void => {
  const pageSections = ds.components.get.byType(COMP_TYPES.SECTION, pageRef);
  const totalAddedSectionsHeight = pageSections.reduce(
    (acc, pageSectionRef) => {
      return ds.components.layout.get(pageSectionRef)!.height + acc;
    },
    0,
  );
  sections.reduce((acc, { structure }) => {
    const layout = structure.layout;
    if (!layout) {
      // TODO: implement for editor3
      return acc;
    }
    layout.y = acc;
    return layout.height + acc;
  }, totalAddedSectionsHeight);
  sections.forEach(({ structure }, index) => {
    ds.components.add(
      pageRef,
      structure,
      undefined,
      index + pageSections.length,
    );
  });
};

export const canSectionPresetBeAdded = (
  section: SectionPresetStructure,
): boolean => {
  return !RESTRICTED_SECTION_CE_TYPES_TO_ADD.has(section.ceType);
};

export const syncAndResetMobileLayout = async (
  documentServices: DocumentServicesObject,
) => {
  documentServices.mobileConversion.syncMobileSite();
  await documentServices.waitForChangesAppliedAsync();
  documentServices.mobileConversion.resetMobileLayoutOnAllPages({
    heuristicStrategy: 'default',
  });
  await documentServices.waitForChangesAppliedAsync();
};

export const getUniquePageName = (
  ds: DocumentServicesObject,
  pageName: string,
): string => {
  let sanitizedPageName = pageName
    .replace(/[^A-Za-z0-9\s]/g, '')
    .replace(/\s+/g, '-');
  const siteHasPageWithUriSeo = (pageUriSEO: string) => {
    return !!ds.pages
      .getPagesData()
      .find((pageData) => pageData.pageUriSEO === pageUriSEO);
  };
  let index = 1;
  while (siteHasPageWithUriSeo(sanitizedPageName)) {
    sanitizedPageName += `-${index}`;
    index++;
  }
  return sanitizedPageName;
};

export const getAppDefIdsNotToPersonalize = (
  ds: DocumentServicesObject,
): string[] => {
  return ds.tpa
    .getPendingApps()
    .concat(ds.platform.getInstalledApps())
    .map(({ appDefinitionId }) => appDefinitionId)
    .filter(Boolean) as string[];
};

export const getSiteGeneratorPlatformOrigin = (
  shouldUseSgInstallOrigin: boolean,
) => {
  return {
    type: EditorType.Classic,
    initiator: InstallInitiator.Editor,
    info: {
      type: shouldUseSgInstallOrigin
        ? InstallationOriginType.SITE_GENERATION
        : InstallationOriginType.SITE_CREATION,
    },
  };
};

export const getMetaSiteInstance = (ds: DocumentServicesObject) => {
  return ds.platform.getAppDataByApplicationId(`-666`).instance;
};

export const getMetaSiteId = (ds: DocumentServicesObject) => {
  return ds.generalInfo.getMetaSiteId();
};

export const addPinElementsToAllDesktopPagesAndGetTheirCompRefs = async (
  ds: DocumentServicesObject,
  pages: AddedPageWithRef[],
  layoutFamilyDefinition: LayoutFamilyDefinition,
): Promise<CompRef[]> => {
  const { pinElements, title } = layoutFamilyDefinition;
  const desktopPinElementsCompRefs: CompRef[] = [];
  try {
    if (pinElements?.length) {
      await ds.transactions.run(async () => {
        pages.forEach(({ pageRef }) => {
          pinElements.forEach((pinElementStructure) => {
            if (!pinElementStructure?.layout?.docked) return;
            const addedPinElement = ds.components.add(
              pageRef,
              pinElementStructure,
            );
            desktopPinElementsCompRefs.push(addedPinElement);
          });
        });
      });
    }
    return desktopPinElementsCompRefs;
  } catch (e) {
    ErrorReporter.captureException(e, {
      tags: {
        siteGenerationApiFlow: true,
      },
      extra: {
        layoutFamily: title,
        pinElementsLength: pinElements?.length,
      },
    });
    return [];
  }
};

export const filterSectionsMatrixByDuplicates = (
  sectionsMatrix: SectionPresetDefinition[][],
): SectionPresetDefinition[][] => {
  const existingSectionsIds: string[] = [];
  return sectionsMatrix.map((sectionsList) => {
    const filteredSectionsList = sectionsList.filter(
      ({ _id }) => !existingSectionsIds.includes(_id),
    );
    existingSectionsIds.push(...filteredSectionsList.map(({ _id }) => _id));
    return filteredSectionsList;
  });
};

export const getFilteredAiMedia = (
  aiMedia?: ImagesMap,
): AiMediaData | undefined => {
  if (!aiMedia) {
    return;
  }
  return Array.from(aiMedia).reduce((acc, [compId, imageDetails]) => {
    const { localUri, staticUri, ...imgData } = imageDetails;
    if (!localUri) return acc;

    return {
      ...acc,
      [compId]: { ...imgData, uri: localUri },
    };
  }, {});
};

export const getContentType = (
  presetStructureContent:
    | SectionPresetStructure
    | HeaderPresetStructure
    | FooterPresetStructure,
): ContentTypes | null => {
  const { structure } = presetStructureContent;

  if (isHeader(structure)) {
    return ExtendedContentTypes.Header;
  } else if (isFooter(structure)) {
    return ExtendedContentTypes.Footer;
  }

  return presetStructureContent.ceType || null;
};

const getLogoComponents = (
  ds: DocumentServicesObject,
  containerCompRef?: CompRef,
) => {
  return ds.components.get
    .byType(COMP_TYPES.PHOTO, containerCompRef)
    .filter(
      (photoRef) =>
        ds.components.features.get(photoRef, 'contentRole')?.fieldRole ===
        PresetContentFieldRole.Logo,
    );
};

export const injectLogoLinksToHeader = (ds: DocumentServicesObject) => {
  const homePageId = ds.homePage.get();
  const logoComponentRefs = getLogoComponents(ds, ds.siteSegments.getHeader());

  if (!logoComponentRefs) {
    return;
  }

  logoComponentRefs.forEach((compRef) => {
    const currentLinkData = ds.components.data.get(compRef)?.link;
    if (!currentLinkData) {
      return;
    }

    ds.components.data.update(compRef, {
      link: {
        ...currentLinkData,
        pageId: `#${homePageId}`,
      },
    });
  });
};

export const getLegalPagesByAppDefId = (
  appDefIds: string[],
  lang: keyof typeof LEGAL_PAGES_BY_LANG,
): string[] => {
  const legalPagesIdsForCurrentLang = LEGAL_PAGES_BY_LANG[lang];

  const legalPagesList = appDefIds.reduce(
    (acc, appDefId) => {
      const appLegalPages = LEGAL_PAGES_TO_APPS[appDefId];

      return Array.from(new Set(acc.concat(appLegalPages || [])));
    },
    DEFAULT_LEGAL_PAGES as (keyof typeof LegalPage)[],
  );

  return legalPagesList.map((page) => legalPagesIdsForCurrentLang[page]);
};

export const getLegalPagesLinksData = (
  legalPages?: SerializedPageStructure[],
): LegalPageLinkData[] | undefined => {
  return legalPages?.map(({ id, data }) => ({
    title: data.title,
    pageId: id,
  }));
};

export const addLegalPages = (
  ds: DocumentServicesObject,
  legalPages?: SerializedPageStructure[],
): LegalPageLinkData[] | undefined => {
  if (!legalPages) return;

  const legalPagesLinksData: LegalPageLinkData[] = [];

  for (const legalPage of legalPages) {
    if (!legalPage.data?.hidePage) {
      legalPage.data.hidePage = true;
    }

    const { pageId } = ds.pages.add(legalPage.data.title, legalPage);

    legalPagesLinksData.push({
      pageId,
      title: legalPage.data.title,
    });
  }

  return legalPagesLinksData;
};
